import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Box, Button, Checkbox, Chip, LinearProgress, Tooltip } from "@material-ui/core";
import * as _ from "lodash";

import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import TableFooter from "@material-ui/core/TableFooter";
import TablePagination from "@material-ui/core/TablePagination";
import { Link, useHistory } from "react-router-dom";
import AppTable from "../Table/AppTable";
import TablePaginationActions from "../Table/TablePaginationActions";
import AppUtil from "../../../../utilities/AppUtil";
import AppButton from "../../../../components/CustomButtons/AppButton";
import AppColor from "../../../../utilities/AppColor";
import InputSearch from "../../../../components/Input/InputSearch";
import AppConfirmDialog from "../../../../components/Dialog/AppConfirmDialog";
import { getCompanyList } from "store/slices/company";
import { fetchCompanyListPaging } from "store/slices/company";
import { fetchDeleteCompany } from "store/slices/company";
import AppConfig from "../../../../config/AppConfig";
import { makeStyles } from "@material-ui/core";
import moment from "moment";
import AppSelect from "components/Select/AppSelect";
import { fetchListSubscription } from "store/slices/subscription";

const headCells = [
  {
    id: "organizationName",
    numeric: "left",
    disablePadding: false,
    label: "label.company_name",
    sort: true,
  },
  {
    id: "subscriptionType",
    numeric: "left",
    disablePadding: false,
    label: "label.subscription",
    sort: true,
  },
  {
    id: "email",
    numeric: "left",
    disablePadding: false,
    label: "label.company_admin_email",
    sort: true,
  },
  {
    id: "phone",
    numeric: "left",
    disablePadding: false,
    label: "label.phone",
    sort: false,
  },
  {
    id: "createdDate",
    numeric: "left",
    disablePadding: false,
    label: "label.created_date",
    sort: true,
  },
  {
    id: "approvalStatus",
    numeric: "left",
    disablePadding: false,
    label: "label.approval_status",
    sort: true,
  },
  {
    id: "status",
    numeric: "left",
    disablePadding: false,
    label: "label.account_status",
    sort: true,
  },
  {
    id: "actions",
    numeric: "center",
    disablePadding: false,
    label: "label.actions",
    sort: false,
  },
];

const styles = makeStyles(() => ({
  shortText: {
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap",
    maxWidth: "200px",
  },
}));

const CompanyTable = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const classes = styles();

  const [queries, setQueries] = useState({
    asc_sort: true,
    page: 1,
    size: 10,
    search_key: "",
    sort_field: "",
    approval_status: "",
  });
  const companyList = useSelector(getCompanyList);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [selected, setSelected] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [searchKey, setSearchKey] = useState("");
  const [subscriptionList, setSubscriptionList] = useState([]);

  const statusOptions = [
    {
      value: "ACTIVE",
      label: t("approval_status.ACTIVE"),
    },
    {
      value: "PENDING",
      label: t("approval_status.PENDING"),
    },
    {
      value: "REJECT",
      label: t("approval_status.REJECT"),
    },
    {
      value: "WAITING_FOR_APPROVAL",
      label: t("approval_status.WAITING_FOR_APPROVAL"),
    },
  ];

  const emptyRows = data.length === 0;

  const _handleDeleteCompanies = async () => {
    const params = {
      ids: selected,
    };
    await dispatch(fetchDeleteCompany(params));
    setShowDeleteModal(false);
    if((data.length === 1 || selected.length === data.length) && queries.page > 1) {
      _loadData({ ...queries, page: queries.page -1});
    } else {
      _loadData({ ...queries, search_key: queries.search_key, approval_status: queries.approval_status, sort_field: queries.sort_field });
    //   if (queries.search_key) {
    //     _loadData({ ...queries, search_key: queries.search_key });
    // } else {
    //     _loadData();
    // }
    }
    setSelected([]);
  };

  const _handleSelect = (id) => {
    if (!selected.includes(id)) {
      setSelected([...selected, id]);
    } else {
      setSelected(selected.filter((item) => item !== id));
    }
  };

  const _handleSelectAll = () => {
    if (data.length === selected.length) {
      setSelected([]);
    } else {
      setSelected(
        _.union(
          selected,
          data.map((item) => item.id)
        )
      );
    }
  };

  const handleChangePage = (event, newPage) => {
    _loadData({ ...queries, page: newPage + 1 });
    AppUtil.scrollToTopById("main-panel");
  };

  const handleChangeRowsPerPage = (event) => {
    _loadData({
      ...queries,
      page: 1,
      size: parseInt(event.target.value),
    });
    AppUtil.scrollToTopById("main-panel");
  };

  const onRequestSort = (event, property) => {
    _loadData({
      ...queries,
      sort_field: property,
      asc_sort:
        property === queries.sort_field ? queries.asc_sort !== true : true,
    });
  };

  const onSearch = (value) => {
    setSearchKey(value);
    _loadData({
      ...queries,
      search_key: value,
      page: 1,
    });
  };

  const onSearching = (value) => {
    setSearchKey(value);
    _loadData({ ...queries, page: 1, search_key: value });
  }

  const _loadData = async ({
    page,
    size,
    search_key,
    asc_sort,
    sort_field,
    approval_status,
  } = {}) => {
    const newQueries = {
      ...queries,
    };
    if (page) {
      newQueries.page = page;
    }
    if (size) {
      newQueries.size = size;
    }
    if (sort_field) {
      newQueries.sort_field = sort_field;
    }
    newQueries.approval_status = approval_status || "";
    newQueries.search_key = search_key || "";
    newQueries.asc_sort = !(!asc_sort || false);
    setLoading(true);
    dispatch(fetchCompanyListPaging(newQueries)).then(({ payload }) => {
      setQueries(newQueries);
      setLoading(false);
      if (payload) {
        setTotalElements(payload.totalElements);
      }
    });
  };

  // eslint-disable-next-line react/prop-types
  const renderSelectField = ({ name, placeholder, options, onChange }) => (
    <AppSelect
      name={name}
      placeholder={t(placeholder)}
      options={options}
      onChange={(option) => {
        const params = {};
        if (option) {
          params[name] = option.value;
          onChange &&
            onChange({
              target: {
                name: name,
                value: option.value,
              },
            });
        } else {
          params[name] = "";
          onChange &&
            onChange({
              target: {
                name: name,
                value: "",
              },
            });
        }
      }}
      allowOverflow={true}
    />
  );

  const handleSelectApprovalStatus = (event) => {
    switch (event.target.value) {
      case "ACTIVE":
        _loadData({ ...queries, page: 1, approval_status: "ACTIVE" });
        break;
      case "PENDING":
        _loadData({ ...queries, page: 1, approval_status: "PENDING" });
        break;
      case "REJECT":
        _loadData({ ...queries, page: 1, approval_status: "REJECT" });
        break;
      case "WAITING_FOR_APPROVAL":
        _loadData({
          ...queries,
          page: 1,
          approval_status: "WAITING_FOR_APPROVAL",
        });
        break;
      case "":
        _loadData({ ...queries, page: 1, approval_status: "" });
        break;
      default:
        _loadData({ ...queries, page: 1, approval_status: "" });
        break;
    }
  };
  const _loadAllSubscriptionPlan = async () => {
    const result = await dispatch(fetchListSubscription({ recurring: "" }));
    if (result.payload) {
      setSubscriptionList(result.payload);
    }
  };
  useEffect(() => {
    _loadData();
    _loadAllSubscriptionPlan();
  }, []);

  useEffect(() => {
    if (companyList) setData(companyList);
  }, [companyList]);

  const handleCreateCompany = () => {
    history.push(`/admin/company/create`);
  };

  return (
    <div>
      <div>
        <AppButton
          label={t("button.create")}
          onClick={() => handleCreateCompany()}
          style={{
            marginRight: "1rem",
          }}
        />
        <AppButton
          label={t("button.delete")}
          buttonColor={AppColor.pink}
          onClick={() => setShowDeleteModal(true)}
          disabled={selected.length === 0}
        />
      </div>
      <Box my={3} style={{ width: "50%", margin: "10px 0px 10px 0px" }}>
        <div style={{ display: "flex", flexDirection: "row" }}>
          <div style={{ width: "42%" }}>
            {renderSelectField({
              name: "status",
              placeholder: "label.status",
              options: statusOptions,
              onChange: (e) => handleSelectApprovalStatus(e),
            })}
          </div>
        </div>
      </Box>
      <Box my={3}>
        <InputSearch onSearch={onSearch} searchText={searchKey}  onSearching={onSearching}/>
      </Box>
      <AppTable
        onRequestSort={onRequestSort}
        order={queries.asc_sort === true ? "asc" : "desc"}
        orderBy={queries.sort_field}
        rowCount={data.length}
        headCells={headCells}
        numSelected={selected.length}
        onSelectAllClick={_handleSelectAll}
      >
        <TableBody>
          {loading && (
            <TableRow>
              <TableCell colSpan={7} padding="none">
                <LinearProgress />
              </TableCell>
            </TableRow>
          )}
          {subscriptionList &&
            data.map((row, index) => (
              <TableRow key={`subscription-list-${index}`}>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selected.indexOf(row.id) !== -1}
                    inputProps={{ "aria-labelledby": row.id }}
                    onClick={() => _handleSelect(row.id)}
                    color="primary"
                  />
                </TableCell>
                <TableCell>
                  {row.organizationName && row.organizationName.length > 22 ? (
                    <Tooltip title={row.organizationName}><p className={classes.shortText}>{row.organizationName}</p></Tooltip>
                  ) : (
                    <span>{row.organizationName}</span>
                  )}
                </TableCell>
                <TableCell>
                  {subscriptionList.map((subs, i) => {
                    if (row.subscriptionId && row.subscriptionId === subs.id) {
                      return (
                        <React.Fragment key={`subscription-list-package-name${i}`}>
                          {subs.packageName && subs.packageName.length > 22 ? (
                            <Tooltip title={subs.packageName}>
                              <p className={classes.shortText}>
                                {subs.packageName}
                              </p>
                            </Tooltip>
                          ) : (
                            <span>{subs.packageName}</span>
                          )}
                        </React.Fragment>
                      );
                    }
                  })}
                </TableCell>
                <TableCell>{row.email}</TableCell>
                <TableCell>{row.phone}</TableCell>
                <TableCell>
                  {moment
                    .utc(row.createdDate)
                    .local()
                    .format("yyyy/MM/DD HH:mm:ss")
                    .toString()}
                </TableCell>
                <TableCell>
                  <Chip
                    style={
                      row.approvalStatus !== AppConfig.APPROVAL_STATUS.PENDING
                        ? {
                            backgroundColor:
                              AppConfig.APPROVAL_STATUS.WAITING_FOR_APPROVAL ===
                              row.approvalStatus
                                ? AppColor.orange
                                : AppConfig.APPROVAL_STATUS.ACTIVE ===
                                  row.approvalStatus
                                ? AppColor.green
                                : AppColor.red,
                            color: AppColor.white,
                          }
                        : {}
                    }
                    size="small"
                    label={t(`approval_status.${row.approvalStatus}`)}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    style={
                      row.status === AppConfig.APPROVAL_STATUS.ACTIVE
                        ? {
                            backgroundColor: AppColor.green,
                            color: AppColor.white,
                          }
                        : {
                            backgroundColor: AppColor.red,
                            color: AppColor.white,
                          }
                    }
                    size="small"
                    label={
                      row.status === AppConfig.APPROVAL_STATUS.ACTIVE
                        ? t(`approval_status.${row.status}`)
                        : t("label.de_activate")
                    }
                  />
                </TableCell>
                <TableCell>
                  <Link
                    to={`/admin/company/detail/${row.id}`}
                    className="text-center"
                  >
                    <Button variant="text">
                      <i className="fas fa-edit" />
                    </Button>
                  </Link>
                </TableCell>
              </TableRow>
            ))}

          {emptyRows > 0 && (
            <TableRow style={{ height: 53 }}>
              <TableCell colSpan={headCells.length + 1} className="text-center">
                {t("label.no_data")}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              colSpan={headCells.length}
              count={totalElements}
              rowsPerPage={queries.size}
              page={queries.page - 1}
              SelectProps={{
                inputProps: { "aria-label": "rows per page" },
                native: true,
              }}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              ActionsComponent={TablePaginationActions}
              labelRowsPerPage={`${t("label.rows_per_page")}:`}
              labelDisplayedRows={({ from, to, count }) =>
                t("label.from_to", {
                  from,
                  to,
                  count: count !== -1 ? count : `${t("text.more_than")} ${to}`,
                })
              }
            />
          </TableRow>
        </TableFooter>
      </AppTable>
      <AppConfirmDialog
        open={showDeleteModal}
        onConfirm={() => _handleDeleteCompanies()}
        onClose={() => setShowDeleteModal(false)}
        title={t("title.delete_company")}
        content={t("text.delete_company")}
      />
    </div>
  );
};

export default CompanyTable;
